
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ActionItem, FilterConfig, FiltersConfig, FilterTypes } from '@next/core-lib/components';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { LocaleService } from '@next/core-lib/i18n';
import { SettingsStorage } from '@next/core-lib/settings';
import { UserSelectors } from '@next/core-lib/user';
import { Action, select, Store } from '@ngrx/store';
import { combineLatest, of, ReplaySubject, Subject } from 'rxjs';
import { map, shareReplay, startWith, switchMap, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { CropFieldAdvice, CustomerProgress, SiteState } from 'src/app/shared/models';
import {
  OpenFertilizerOrderPlanning,
  OpenPlan,
  ResetCropFieldSyncStatus,
  SetCropYear,
  ToggleCustomerProgressFertilization,
  ToggleCustomerProgressGBM,
} from 'src/app/store/actions';
import * as AppSelectors from 'src/app/store/selectors';

import { selectCustomerProgress } from 'src/app/store/selectors';

import { Environment } from 'src/environments/environment';

import { SyncStatusEnum } from '../advice/models';
import {
  CopyDefaultCropCareAdvice,
  CopyDefaultFertilizationAdvice,
  CreateAdviceReport,
  DeleteMultipleCropFields,
} from './store/actions';
import {
  OpenCompanySettingsDialog,
  OpenEditAllFertilizationSettingsDialog,
  TriggerEditFertilizationBalanceSettingsDialog,
} from '../advice/store/actions';
import { CropAid } from '../crop-aid/crop-aid.model';
import { selectCropAids } from '../crop-aid/store/crop-aid.selectors';

import { TreatmentType } from '../sales-order-period/models/sales-order-period.models';

enum AdviceAction {
  editGBM = 'editGBM',
  editFertilization = 'editFertilization',
}

enum ButtonAction {
  customerProgressGbm = 'customerProgressGbm',
  customerProgressFertilization = 'customerProgressFertilization',
  editAllFertilizationSettings = 'editAllFertilizationSettings',
  companySettings = 'companySettings',
  openFertilizerOrderPlanning = 'openFertilizerOrderPlanning',
  syncStatusChecked = 'syncStatusChecked',
  copyDefaultCropCareAdvice = 'copyDefaultCropCareAdvice',
  copyDefaultFertilizationAdvice = 'copyDefaultFertilizationAdvice',
  openPotatoCropFields = 'openPotatoCropFields',
}

interface ButtonItem {
  viewValue: string;
  key: string;
  icon: string;
  svgIcon?: string;
}

@Component({
  selector: 'app-overview',
  templateUrl: './overview.component.html',
  styleUrl: './overview.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class OverviewComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly router = inject(Router);
  private readonly dialogService = inject(DialogService);
  readonly locale :LocaleService = inject(LocaleService);
  private readonly env = inject<Environment>(EnvironmentInjector);
  private readonly cdr = inject(ChangeDetectorRef);

  private readonly destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  private readonly cropYearChanged$ = new Subject<number>();
  readonly searchChanged$ = new Subject<string>();
  readonly organicChanged$ = new Subject<boolean | null>();
  readonly planetProofChanged$ = new Subject<boolean | null>();
  readonly groundWaterPreservationChanged$ = new Subject<boolean | null>();
  readonly sandySoilChanged$ = new Subject<boolean | null>();
  readonly nvgChanged$ = new Subject<boolean | null>();
  readonly gwbNCutChanged$ = new Subject<boolean | null>();
  readonly rentedFieldChanged$ = new Subject<boolean | null>();
  availableCropFieldAdvices$ = this.store.pipe(
    select(AppSelectors.selectAvailableCropFieldAdvices()),
  );
  cropFieldAdvices$ = this.store.pipe(select(AppSelectors.selectCropFieldAdvices));
  genericCropAids$ = this.store.pipe(select(selectCropAids));
  cropAids: CropAid[];
  areAllCropFieldsSelected = false;
  hasFilteredCropFieldAdvices$ = this.store.pipe(select(AppSelectors.hasFilteredCropFieldAdvices));
  cropYear$ = this.store.pipe(select(AppSelectors.getCropYear));
  availableCropYears$ = this.store.pipe(select(AppSelectors.getAvailableCropYears));
  customer$ = this.store.pipe(select(UserSelectors.selectCustomer));
  defaultCropYear$ = this.store.pipe(select(AppSelectors.getDefaultCropYear));
  searchString$ = this.searchChanged$.pipe(map((searchString) => searchString));
  customerProgress$ = this.store.pipe(select(selectCustomerProgress));
  hasSearch$ = this.searchString$.pipe(
    map((query) => !!query),
    shareReplay(1),
  );
  showCropFieldAdvices$ = combineLatest([
    this.searchString$,
    this.availableCropFieldAdvices$,
    this.organicChanged$.pipe(startWith(null)),
    this.planetProofChanged$.pipe(startWith(null)),
    this.groundWaterPreservationChanged$.pipe(startWith(null)),
    this.sandySoilChanged$.pipe(startWith(null)),
    this.nvgChanged$.pipe(startWith(null)),
    this.gwbNCutChanged$.pipe(startWith(null)),
    this.rentedFieldChanged$.pipe(startWith(null)),
  ]).pipe(
    takeUntil(this.destroyed$),
    map(([query, cropFieldAdvices, organic, planetProof, groundWaterPreservation, sandySoil, nvg, gwbNCut, rentedField]) => {
      let result = cropFieldAdvices;

      if (organic) result = result.filter((a) => a.isSkal);
      if (planetProof) result = result.filter((a) => a.isPlanetProof);
      if (groundWaterPreservation) result = result.filter((a) => a.isGroundWaterPreservation);
      if (sandySoil) result = result.filter((a) => a.isSandySoil);
      if (nvg) result = result.filter((a) => a.isNutrientsPollutedArea);
      if (gwbNCut) result = result.filter((a) => a.isGroundWaterPreservationWithNitrogenCuts);
      if (rentedField) result = result.filter((a) => a.isRentedField);

      if (query) {
        let foundCropFieldAdvicesByCropAids: CropFieldAdvice[] = [];
        if (this.cropAids && this.cropAids.length > 0) {
          const foundCropAidIds = this.cropAids
            .filter((cropAid) => cropAid.name.toLowerCase().includes(query.toLowerCase()))
            .map((x) => x.id);

          foundCropFieldAdvicesByCropAids = result.filter((advices) =>
            advices.treatments.some(
              (treatment) =>
                treatment.aidAdvices.filter((aidAdvice) =>
                  foundCropAidIds.includes(aidAdvice.cropAidId),
                ).length > 0,
            ),
          );
        }

        const cropFieldsByNameCropNameCropVariety = result.filter(
          (cropFieldAdvice) =>
            [
              cropFieldAdvice.name,
              cropFieldAdvice.crop.name,
              cropFieldAdvice.cropVariety != null ? cropFieldAdvice.cropVariety.name : '',
            ]
              .map(
                (element) => element?.toString().toLowerCase().indexOf(query.toLowerCase()) !== -1,
              )
              .filter(Boolean).length > 0,
        );

        return this.filterAdvicesUniqueByAdviceId([
          ...cropFieldsByNameCropNameCropVariety,
          ...foundCropFieldAdvicesByCropAids,
        ]);
      }

      return result;
    }),
    shareReplay(1),
  );
  isEmpty$ = this.showCropFieldAdvices$.pipe(
    map((cropFieldAdvices) => !cropFieldAdvices?.length),
    shareReplay(1),
  );
  filterReady = true;
  filterConfig: FilterConfig<FiltersConfig> = {
    filters: {
      year: {
        label: 'year',
        type: FilterTypes.Select,
        options: this.availableCropYears$.pipe(
          map((availableYears) =>
            availableYears.map((availableYear) => ({
              value: availableYear,
              viewValue: availableYear.toString(),
            })),
          ),
        ),
        value: this.cropYear$,
        defaultValue: this.defaultCropYear$,
        changes: this.cropYearChanged$,
      },
      search: {
        label: 'search',
        type: FilterTypes.Text,
        changes: this.searchChanged$,
        store: {
          contextName: 'advice-search-filter',
          storage: SettingsStorage.AppState,
          key: 'search-string',
        },
      },
      organic: {
        label: this.locale.translate('organic'),
        type: FilterTypes.Toggle,
        priority: false,
        changes: this.organicChanged$,
      },
      planetProof: {
        label: this.locale.translate('planetProof'),
        type: FilterTypes.Toggle,
        priority: false,
        changes: this.planetProofChanged$,
      },
      isGroundWaterPreservation: {
        label: this.locale.translate('isGroundWaterPreservation'),
        type: FilterTypes.Toggle,
        priority: false,
        changes: this.groundWaterPreservationChanged$,
      },
      sandySoil: {
        label: this.locale.translate('sandySoil'),
        type: FilterTypes.Toggle,
        priority: false,
        changes: this.sandySoilChanged$,
      },
      isNutrientsPollutedArea: {
        label: this.locale.translate('isNutrientsPollutedArea'),
        type: FilterTypes.Toggle,
        priority: false,
        changes: this.nvgChanged$,
      },
      isGroundWaterPreservationWithNitrogenCuts: {
        label: this.locale.translate('isGroundWaterPreservationWithNitrogenCuts'),
        type: FilterTypes.Toggle,
        priority: false,
        changes: this.gwbNCutChanged$,
      },
      isRentedField: {
        label: this.locale.translate('isRentedField'),
        type: FilterTypes.Toggle,
        priority: false,
        changes: this.rentedFieldChanged$,
      },
    },
  };

  private updateToggleFilterLabels(): void {
    const f = this.filterConfig.filters;
    f['organic'].label = this.locale.translate('organic');
    f['planetProof'].label = this.locale.translate('planetProof');
    f['isGroundWaterPreservation'].label = this.locale.translate('isGroundWaterPreservation');
    f['sandySoil'].label = this.locale.translate('sandySoil');
    f['isNutrientsPollutedArea'].label = this.locale.translate('isNutrientsPollutedArea');
    f['isGroundWaterPreservationWithNitrogenCuts'].label = this.locale.translate('isGroundWaterPreservationWithNitrogenCuts');
    f['isRentedField'].label = this.locale.translate('isRentedField');
    this.filterConfig = { ...this.filterConfig };
  }

  adviceActions$ = this.cropFieldAdvices$.pipe(
    takeUntil(this.destroyed$),
    map((advices) =>
      advices.reduce(
        (dict: { [id: number]: ActionItem[] }, cropFieldAdvice) => {
          dict[cropFieldAdvice.cropFieldId] = this.createAdviceActions(cropFieldAdvice);
          return dict;
        },
        {},
      ),
    ),
  );

  buttonItems$ = combineLatest([
    this.customerProgress$,
    this.cropFieldAdvices$,
  ]).pipe(
    map(
      ([customerProgress, cropFieldAdvice]: [CustomerProgress | undefined, CropFieldAdvice[]]) => {
        const copyDefaultCropCareAdviceButton: ButtonItem = {
          viewValue: 'copyDefaultAdvice.copyDefaultCropCareAdvice',
          key: ButtonAction.copyDefaultCropCareAdvice,
          icon: 'convert-to-text',
          svgIcon: 'convert-to-text',
        };

        const copyDefaultFertilizationAdviceButton: ButtonItem = {
          viewValue: 'copyDefaultAdvice.copyDefaultFertilizationAdvice',
          key: ButtonAction.copyDefaultFertilizationAdvice,
          icon: 'convert-to-text',
          svgIcon: 'convert-to-text',
        };

        const syncStatusCheckedButton: ButtonItem = {
          viewValue: 'syncStatusChecked',
          key: ButtonAction.syncStatusChecked,
          icon: 'playlist_add_check_circle',
        };

        const customerProgressGbmButton: ButtonItem = {
          viewValue: 'customerProgressGbm',
          key: ButtonAction.customerProgressGbm,
          icon: customerProgress?.processes?.fieldAdviceCropProtectionAdvicesCompleted
            ? 'check_box'
            : 'check_box_outline_blank',
        };
        const customerProgressFertilizationButton: ButtonItem = {
          viewValue: 'customerProgressFertilization',
          key: ButtonAction.customerProgressFertilization,
          icon: customerProgress?.processes?.fieldAdviceFertilizationAdvicesCompleted
            ? 'check_box'
            : 'check_box_outline_blank',
        };
        const editAllFertilizationSettingsButton: ButtonItem = {
          viewValue: 'editAllFertilizationSettings',
          key: ButtonAction.editAllFertilizationSettings,
          icon: 'compost',
          svgIcon: 'compost-settings',
        };
        const companySettingsButton: ButtonItem = {
          viewValue: 'companySettings',
          key: ButtonAction.companySettings,
          icon: 'warehouse-settings',
          svgIcon: 'warehouse-settings',
        };

        const orderFertilizer: ButtonItem = {
          viewValue: 'orderFertilizer',
          key: ButtonAction.openFertilizerOrderPlanning,
          icon: 'shopping_cart',
        };

        const openPotatoCropFields: ButtonItem = {
          viewValue: 'openPotatoCropFields',
          key: ButtonAction.openPotatoCropFields,
          icon: 'balance',
          svgIcon:'balance',
        };
        let baseList = cropFieldAdvice?.find((ca) => ca.syncStatus)
          ? [syncStatusCheckedButton]
          : [];

        if (customerProgress) {
          baseList = baseList.concat([
            customerProgressGbmButton,
            customerProgressFertilizationButton,
            orderFertilizer,
            editAllFertilizationSettingsButton,
            companySettingsButton,
          ]);
        }
        if (this.env.SHOW_POTATO_CROP_FIELDS_BUTTON) {
          baseList.splice(-1, 0, openPotatoCropFields);
        }

        return [copyDefaultCropCareAdviceButton, copyDefaultFertilizationAdviceButton, ...baseList];
      },
    ),
  );

  constructor() {}

  ngOnInit() {
    this.cropYearChanged$
      .pipe(withLatestFrom(this.cropYear$), takeUntil(this.destroyed$))
      .subscribe(([newYear, existingYear]) => {
        if (newYear && newYear !== existingYear) {
          this.store.dispatch(new SetCropYear(newYear));
        }
      });

    this.locale.localeDataChanged$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.updateToggleFilterLabels();
        this.filterReady = false;
        this.cdr.detectChanges();
        this.filterReady = true;
        this.cdr.markForCheck();
      });

    this.getGenericCropAids();
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  adviceAction(key: string, cropFieldAdvice: CropFieldAdvice) {
    switch (key) {
    case AdviceAction.editFertilization:
      this.router.navigate(
        [
          '/advice',
          cropFieldAdvice.cropYear,
          cropFieldAdvice.cropFieldId,
          TreatmentType.Fertilization,
        ],
        {
          queryParamsHandling: 'preserve',
        },
      );
      break;
    case AdviceAction.editGBM:
      this.router.navigate(
        ['/advice', cropFieldAdvice.cropYear, cropFieldAdvice.cropFieldId, TreatmentType.GBM],
        {
          queryParamsHandling: 'preserve',
        },
      );
      break;
    }
  }

  buttonAction(data: string) {
    switch (data) {
    case ButtonAction.customerProgressGbm:
      this.customerProgressGbmToggle();
      break;
    case ButtonAction.customerProgressFertilization:
      this.customerProgressFertilizationToggle();
      break;
    case ButtonAction.editAllFertilizationSettings:
      this.editAllFertilizationSettingsToggle();
      break;
    case ButtonAction.companySettings:
      this.companySettingsToggle();
      break;
    case ButtonAction.openFertilizerOrderPlanning:
      this.openFertilizerOrderPlanning();
      break;
    case ButtonAction.syncStatusChecked:
      this.resetCropFieldSyncStatus();
      break;
    case ButtonAction.copyDefaultCropCareAdvice:
      this.openCopyDefaultCropCareAdviceModal();
      break;
    case ButtonAction.copyDefaultFertilizationAdvice:
      this.openCopyDefaultFertilizationAdviceModal();
      break;
    case ButtonAction.openPotatoCropFields:
      this.editFertilizationBalanceSettingsToggle();
      break;
    }
  }

  deselect() {
    this.router.navigate(['/'], { queryParamsHandling: 'preserve' });
  }

  createAdviceReport() {
    this.store.dispatch(new CreateAdviceReport());
  }

  openFieldPlan() {
    this.store.dispatch(new OpenPlan());
  }

  gbmTreatments$(adviceId: number) {
    return this.store.pipe(select(AppSelectors.selectGBMTreatments(adviceId)));
  }

  fertilizationTreatments$(adviceId: number) {
    return this.store.pipe(select(AppSelectors.selectFertilizationTreatments(adviceId)));
  }

  customerProgressGbmToggle() {
    this.store.dispatch(new ToggleCustomerProgressGBM());
  }

  customerProgressFertilizationToggle() {
    this.store.dispatch(new ToggleCustomerProgressFertilization());
  }

  editAllFertilizationSettingsToggle() {
    this.store.dispatch(new OpenEditAllFertilizationSettingsDialog());
  }

  editFertilizationBalanceSettingsToggle() {
    this.store.dispatch(new TriggerEditFertilizationBalanceSettingsDialog());
  }

  companySettingsToggle() {
    this.store.dispatch(new OpenCompanySettingsDialog());
  }

  openFertilizerOrderPlanning() {
    this.store.dispatch(new OpenFertilizerOrderPlanning());
  }

  resetCropFieldSyncStatus() {
    this.store.dispatch(new ResetCropFieldSyncStatus());
  }

  openCopyDefaultCropCareAdviceModal() {
    this.runCopyWithOptionalConfirm(() => this.copyDefaultCropCareAdvices(), TreatmentType.GBM);
  }

  openCopyDefaultFertilizationAdviceModal() {
    this.runCopyWithOptionalConfirm(() => this.copyDefaultFertilizationAdvices(), TreatmentType.Fertilization);
  }

  copyDefaultCropCareAdvices() {
    this.copyDefaultAdvices((id) => new CopyDefaultCropCareAdvice(id));
  }

  copyDefaultFertilizationAdvices() {
    this.copyDefaultAdvices((id) => new CopyDefaultFertilizationAdvice(id));
  }

  /** Ask for confirmation only when any advice already has treatments. */
  private confirmIfExistingAdvices$(cropFieldAdvices: CropFieldAdvice[], treatmentType: TreatmentType) {
    const hasExisting = cropFieldAdvices
      .flatMap((advice) => advice.treatments)
      .some((ca) => ca.treatmentType === treatmentType);

    if (!hasExisting) {
      return of(true);
    }

    const title = treatmentType === TreatmentType.GBM ?
      this.locale.translate('copyDefaultAdvice.copyDefaultCropCareAdvice') :
      this.locale.translate('copyDefaultAdvice.copyDefaultFertilizationAdvice');

    const description = treatmentType === TreatmentType.GBM ?
      this.locale.translate('copyDefaultAdvice.existingCropCareAdvicesFound') :
      this.locale.translate('copyDefaultAdvice.existingFertilizationAdvicesFound');

    return this.dialogService.confirm({
      title: title,
      text: description,
      pushHistoryState: false,
    }).pipe(
      take(1),
      map((x) => x.status === DIALOG_RETURN_TYPES.confirm),
    );
  }

  /** Generic handler to run a copy action after optional confirmation. */
  private runCopyWithOptionalConfirm(copyAction: () => void, treatmentType: TreatmentType) {
    this.cropFieldAdvices$
      .pipe(
        take(1),
        switchMap((advices) => this.confirmIfExistingAdvices$(advices, treatmentType)),
        take(1),
      )
      .subscribe((shouldCopy) => {
        if (shouldCopy) {
          copyAction();
        }
      });
  }

  /** Generic dispatcher for "copy default ..." actions bound to a customer id. */
  private copyDefaultAdvices(dispatchFactory: (customerId: number) => Action) {
    this.customer$
      .pipe(take(1))
      .subscribe((customer) => {
        if (customer) {
          this.store.dispatch(dispatchFactory(customer.id));
        }
      });
  }

  createAdviceActions(cropFieldAdvice: CropFieldAdvice) {
    const adviceActions = [
      {
        icon: cropFieldAdvice.fertilizationCorrupt
          ? 'warning'
          : cropFieldAdvice.fertilizationCompleted
            ? 'done'
            : 'edit',
        viewValue: 'Fertilization',
        key: AdviceAction.editFertilization,
      },
      {
        icon: cropFieldAdvice.gbmCorrupt
          ? 'warning'
          : cropFieldAdvice.gbmCompleted
            ? 'done'
            : 'edit',
        viewValue: 'GBM',
        key: AdviceAction.editGBM,
      },
    ];
    return adviceActions;
  }

  getSyncStatusColor(cropFieldAdvice: CropFieldAdvice): string {
    switch (cropFieldAdvice.syncStatus) {
    case SyncStatusEnum.New:
      return 'new';
    case SyncStatusEnum.Updated:
      return 'updated';
    case SyncStatusEnum.NotPresent:
      return 'not-present';
    default:
      return 'already-existing';
    }
  }

  getSyncStatusIconTooltipText(cropFieldAdvice: CropFieldAdvice) {
    switch (cropFieldAdvice.syncStatus) {
    case SyncStatusEnum.New:
      return `${this.locale.translate('fieldAdviceSyncStatus.new')} ${
        this.getProviderName(cropFieldAdvice.syncSource)
      }`;
    case SyncStatusEnum.Updated:
      return `${this.locale.translate('fieldAdviceSyncStatus.changedBy')} ${
        this.getProviderName(cropFieldAdvice.syncSource)
      }`;
    case SyncStatusEnum.NotPresent:
      return `${this.locale.translate('fieldAdviceSyncStatus.notPresentIn')} ${
        this.getProviderName(cropFieldAdvice.syncSource)
      }`;

    default:
      return '';
    }
  }

  getProviderName(provider: string): string {
    if(provider === 'CropVision') {
      return 'AgroVision';
    }

    return provider;
  }

  selectedRowsCount(cropFields: CropFieldAdvice[]): number {
    return cropFields.filter((x) => x.selected).length;
  }

  selectAllChanged(selectAllValue: boolean, cropFields: CropFieldAdvice[]) {
    cropFields.forEach((cropField) => cropField.selected = selectAllValue);
    this.areAllCropFieldsSelected = selectAllValue;
  }

  someSelected(cropFields: CropFieldAdvice[]) {
    return cropFields.find((cf) => cf.selected === true) && !this.areAllCropFieldsSelected;
  }

  deleteCropFields(cropFields: CropFieldAdvice[]): void {
    const selectedCropFields = cropFields.filter((cf) => cf.selected);
    const hasMultipleYearsCropFields = selectedCropFields.some((cf) => {
      const startDate = new Date(cf.startDate);
      const endDate = new Date(cf.endDate);
      return startDate.getFullYear() !== endDate.getFullYear();
    });

    const hasCropsWithAdvices = selectedCropFields.some((cf) => cf.treatments && cf.treatments.length > 0);

    if(hasMultipleYearsCropFields){
      this.dialogService
        .confirm({
          title: this.locale.translate('deleteCropFields.warningTitle'),
          text: `${this.locale.translate(
            hasMultipleYearsCropFields? 'deleteCropFields.multipleYearsWarningText' : '' ,
          )} <br/> ${this.locale.translate(
            hasCropsWithAdvices? 'deleteCropFields.hasAdvicesWarningText' : '' ,
          )}`,
          pushHistoryState: false,
        })
        .pipe(take(1))
        .subscribe((x) => {
          if (x.status === DIALOG_RETURN_TYPES.confirm) {
            this.store.dispatch(new DeleteMultipleCropFields(selectedCropFields.map((scf) => (scf.cropFieldId))));
          }
        });
    } else {
      this.store.dispatch(new DeleteMultipleCropFields(selectedCropFields.map((scf) => (scf.cropFieldId))));
    }
  }

  cropFieldsHasSyncStatus(cropFieldAdvices: CropFieldAdvice[]): boolean{
    return cropFieldAdvices?.find((ca) => ca.syncStatus) !== undefined;
  }

  private getGenericCropAids() {
    this.genericCropAids$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((cropAids) => (this.cropAids = cropAids));
  }

  private filterAdvicesUniqueByAdviceId(advices: CropFieldAdvice[]) {
    const uniqueArray: CropFieldAdvice[] = [];
    const uniqueIds = new Set();

    advices.forEach((advice) => {
      if (!uniqueIds.has(advice.adviceId)) {
        uniqueIds.add(advice.adviceId);
        uniqueArray.push(advice);
      }
    });

    return uniqueArray;
  }
}
